import React, { useState, useMemo } from 'react';
import { useAppointment } from '../context/AppointmentContext';
import {
  Calendar, Clock, User, Mail, Phone, MessageSquare,
  ChevronLeft, ChevronRight, CheckCircle, Sparkles, Briefcase
} from 'lucide-react';
import { SERVICE_CATEGORIES } from '../data/services';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── Mini Calendar ─────────────────────────────────────────────────────────
const MiniCalendar = ({ selectedDate, onSelectDate, isDateAvailable }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const toDateStr = (day) => {
    const mm = String(viewMonth + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${viewYear}-${mm}-${dd}`;
  };

  return (
    <div className="appt-calendar">
      {/* Header */}
      <div className="appt-cal-header">
        <button onClick={prevMonth} className="appt-cal-nav" aria-label="Previous month">
          <ChevronLeft size={18} />
        </button>
        <span className="appt-cal-title">{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} className="appt-cal-nav" aria-label="Next month">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Weekday labels */}
      <div className="appt-cal-grid">
        {DAYS.map(d => (
          <div key={d} className="appt-cal-weekday">{d}</div>
        ))}
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const dateStr = toDateStr(day);
          const dateObj = new Date(viewYear, viewMonth, day);
          const isPast = dateObj < today;
          const isAvail = !isPast && isDateAvailable(dateStr);
          const isSelected = selectedDate === dateStr;
          const isToday = dateObj.getTime() === today.getTime();

          return (
            <button
              key={dateStr}
              onClick={() => isAvail && onSelectDate(dateStr)}
              disabled={!isAvail}
              className={`appt-cal-day
                ${isToday ? 'appt-cal-today' : ''}
                ${isSelected ? 'appt-cal-selected' : ''}
                ${!isAvail ? 'appt-cal-disabled' : 'appt-cal-available'}
              `}
              aria-label={`${MONTHS[viewMonth]} ${day}, ${viewYear}`}
              aria-pressed={isSelected}
            >
              {day}
              {isAvail && !isSelected && <span className="appt-cal-dot" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Step Indicator ─────────────────────────────────────────────────────────
const StepIndicator = ({ step, lang }) => {
  const steps = lang === 'np'
    ? ['मिति र समय छान्नुहोस्', 'तपाईंको विवरण', 'पुष्टि गर्नुहोस्']
    : ['Choose Date & Time', 'Your Details', 'Confirm'];
  return (
    <div className="appt-steps">
      {steps.map((label, i) => (
        <React.Fragment key={i}>
          <div className={`appt-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
            <div className="appt-step-circle">
              {step > i + 1 ? <CheckCircle size={16} /> : i + 1}
            </div>
            <span className="appt-step-label">{label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`appt-step-line ${step > i + 1 ? 'done' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── Main Appointment Page ──────────────────────────────────────────────────
const Appointment = () => {
  const { getAvailableSlots, isDateAvailable, addAppointment, servicesList } = useAppointment();

  const [lang, setLang] = useState('en'); // 'en' | 'np'
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null); // null = show all
  const [catDropOpen, setCatDropOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(''); // stores service id
  const [selectedServiceLabel, setSelectedServiceLabel] = useState(''); // English label for storage
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [bookedAppt, setBookedAppt] = useState(null);

  const availableSlots = useMemo(
    () => getAvailableSlots(selectedDate),
    [selectedDate, getAvailableSlots]
  );

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
  };

  const validateStep1 = () => selectedDate && selectedTime && selectedService;

  const validateStep2 = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = 'Valid email is required';
    if (!form.phone.trim()) newErrors.phone = 'Phone number is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = () => {
    const appt = addAppointment({
      ...form,
      date: selectedDate,
      time: selectedTime,
      service: selectedServiceLabel,
    });
    setBookedAppt(appt);
    setSubmitted(true);
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (submitted && bookedAppt) {
    return (
      <div className="appt-page">
        <div className="appt-success-wrap">
          <div className="appt-success-icon">
            <CheckCircle size={64} />
          </div>
          <h2>{lang === 'np' ? 'अपोइन्टमेन्ट पुष्टि भयो!' : 'Appointment Confirmed!'}</h2>
          <p className="appt-success-sub">
            {lang === 'np'
              ? 'तपाईंको बुकिङ अनुरोध पठाइयो। प्रकाश उपाध्याय छिट्टै पुष्टि गर्नुहुनेछ।'
              : 'Your booking request has been submitted. Prakash Upadhyaya will confirm shortly.'}
          </p>
          <div className="appt-success-card">
            <div className="appt-success-row"><span>{lang === 'np' ? 'सन्दर्भ नम्बर' : 'Reference ID'}</span><strong>{bookedAppt.id}</strong></div>
            <div className="appt-success-row"><span>{lang === 'np' ? 'नाम' : 'Name'}</span><strong>{bookedAppt.name}</strong></div>
            <div className="appt-success-row"><span>{lang === 'np' ? 'सेवा' : 'Service'}</span><strong>{bookedAppt.service}</strong></div>
            <div className="appt-success-row"><span>{lang === 'np' ? 'मिति' : 'Date'}</span><strong>{formatDateDisplay(bookedAppt.date)}</strong></div>
            <div className="appt-success-row"><span>{lang === 'np' ? 'समय' : 'Time'}</span><strong>{bookedAppt.time}</strong></div>
            <div className="appt-success-row"><span>{lang === 'np' ? 'स्थिति' : 'Status'}</span>
              <strong className="appt-badge-pending">{lang === 'np' ? 'पुष्टिको प्रतीक्षामा' : 'Pending Confirmation'}</strong>
            </div>
          </div>
          <button
            className="appt-btn-primary"
            onClick={() => { setSubmitted(false); setStep(1); setSelectedDate(''); setSelectedTime(''); setSelectedService(''); setSelectedServiceLabel(''); setForm({ name: '', email: '', phone: '', message: '' }); }}
          >
            {lang === 'np' ? 'अर्को अपोइन्टमेन्ट बुक गर्नुहोस्' : 'Book Another Appointment'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="appt-page">
      {/* Hero Header */}
      <div className="appt-hero">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="appt-hero-badge">
            <Calendar size={16} />
            <span>{lang === 'np' ? 'सत्र बुक गर्नुहोस्' : 'Book a Session'}</span>
          </div>
          {/* Language Toggle */}
          <div className="flex items-center gap-1 bg-white/20 backdrop-blur rounded-xl p-1">
            <button onClick={() => setLang('en')} className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${lang === 'en' ? 'bg-white text-[#1b126c] shadow-sm' : 'text-white/70 hover:text-white'}`}>English</button>
            <button onClick={() => setLang('np')} className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${lang === 'np' ? 'bg-white text-[#1b126c] shadow-sm' : 'text-white/70 hover:text-white'}`}>नेपाली</button>
          </div>
        </div>
        <h1 className="appt-hero-title">
          {lang === 'np' ? 'अपोइन्टमेन्ट' : 'Schedule an'} <span>{lang === 'np' ? 'बुक गर्नुहोस्' : 'Appointment'}</span>
        </h1>
        <p className="appt-hero-sub">
          {lang === 'np'
            ? 'तपाईंलाई मिल्ने मिति र समय छान्नुहोस् — हामी तपाईंको सेवामा छौं।'
            : 'Pick a date and time that works for you — we are here to serve you.'}
        </p>
      </div>

      <div className="appt-container">
        {/* Step Indicator */}
        <StepIndicator step={step} lang={lang} />

        {/* Step 1: Date, Time, Service */}
        {step === 1 && (
          <div className="appt-step-content appt-fade-in">
            <div className="appt-step1-grid">
              {/* Calendar */}
              <div className="appt-card">
                <div className="appt-card-header">
                  <Calendar size={20} />
                  <h3>{lang === 'np' ? 'मिति छान्नुहोस्' : 'Select Date'}</h3>
                </div>
                <MiniCalendar
                  selectedDate={selectedDate}
                  onSelectDate={handleDateSelect}
                  isDateAvailable={isDateAvailable}
                />
                {selectedDate && (
                  <p className="appt-selected-info">
                    📅 {formatDateDisplay(selectedDate)}
                  </p>
                )}
              </div>

              {/* Time Slots & Service */}
              <div className="appt-right-col">
                {/* Time Slots */}
                <div className="appt-card">
                  <div className="appt-card-header">
                    <Clock size={20} />
                    <h3>{lang === 'np' ? 'समय छान्नुहोस्' : 'Select Time Slot'}</h3>
                  </div>
                  {!selectedDate ? (
                    <p className="appt-hint">{lang === 'np' ? 'उपलब्ध समय हेर्न पहिले मिति छान्नुहोस्।' : 'Please select a date first to see available time slots.'}</p>
                  ) : availableSlots.length === 0 ? (
                    <p className="appt-hint appt-hint-warn">
                      {lang === 'np' ? 'यस मितिमा उपलब्ध समय छैन। अर्को दिन छान्नुहोस्।' : 'No available slots for this date. Please choose another day.'}
                    </p>
                  ) : (
                    <div className="appt-slots-grid">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot}
                          onClick={() => setSelectedTime(slot)}
                          className={`appt-slot ${selectedTime === slot ? 'selected' : ''}`}
                          aria-pressed={selectedTime === slot}
                        >
                          <Clock size={14} />
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Single Service Card with dropdown category chooser */}
                {(() => {
                  const activeCategories = (servicesList && servicesList.length > 0) ? servicesList : SERVICE_CATEGORIES;
                  // Show all services if no category selected, else filter
                  const servicesToShow = selectedCategory
                    ? activeCategories.find(c => c.id === selectedCategory)?.services || []
                    : activeCategories.flatMap(c => c.services || []);
                  const activeCat = activeCategories.find(c => c.id === selectedCategory);
                  return (
                    <div className="appt-card" style={{ position: 'relative' }}>

                      {/* Header row */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="appt-card-header" style={{ marginBottom: 0 }}>
                          <Briefcase size={20} />
                          <h3>{lang === 'np' ? 'सेवा छान्नुहोस्' : 'Select Service'}</h3>
                        </div>

                        {/* Choose Category dropdown trigger — styled like card header */}
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={() => setCatDropOpen(o => !o)}
                            className="flex items-center gap-1.5 text-sm font-bold transition-all duration-200 hover:opacity-70"
                            style={{ color: '#1b126c' }}
                          >
                            <span>{lang === 'np' ? 'श्रेणी छान्नुहोस्' : 'Choose category'}</span>
                            <svg
                              width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                              style={{ transform: catDropOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>

                          {/* Dropdown panel */}
                          {catDropOpen && (
                            <div
                              style={{
                                position: 'absolute',
                                top: 'calc(100% + 8px)',
                                right: 0,
                                zIndex: 50,
                                background: '#fff',
                                borderRadius: '14px',
                                boxShadow: '0 8px 32px rgba(27,18,108,0.13)',
                                border: '1.5px solid #e8eaf0',
                                minWidth: '220px',
                                overflow: 'hidden',
                              }}
                            >
                              {activeCategories.map((cat, idx) => (
                                <button
                                  key={cat.id}
                                  onClick={() => {
                                    setSelectedCategory(cat.id);
                                    setSelectedService('');
                                    setSelectedServiceLabel('');
                                    setCatDropOpen(false);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-left transition-all duration-150"
                                  style={{
                                    background: selectedCategory === cat.id ? cat.bg : 'transparent',
                                    color: selectedCategory === cat.id ? cat.color : '#374151',
                                    borderBottom: idx < activeCategories.length - 1 ? '1px solid #f1f5f9' : 'none',
                                    fontWeight: selectedCategory === cat.id ? 700 : 500,
                                  }}
                                >
                                  <span style={{ fontSize: '18px' }}>{cat[lang]?.icon || '🪔'}</span>
                                  <span>{cat[lang]?.label || cat.id}</span>
                                  {selectedCategory === cat.id && (
                                    <svg style={{ marginLeft: 'auto', color: cat.color }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Services grid — all or filtered */}
                      <div className="appt-services-grid">
                        {servicesToShow.map((svc) => {
                          const t = svc[lang] || svc.en || {};
                          // Find this service's category for correct highlight color
                          const svcCat = activeCategories.find(c => (c.services || []).some(s => s.id === svc.id));
                          return (
                            <button
                              key={svc.id}
                              onClick={() => {
                                setSelectedService(svc.id);
                                setSelectedServiceLabel(svc.en?.title || t.title);
                                setCatDropOpen(false);
                              }}
                              className={`appt-service-btn ${selectedService === svc.id ? 'selected' : ''}`}
                              aria-pressed={selectedService === svc.id}
                              style={selectedService === svc.id && svcCat
                                ? { borderColor: svcCat.color, color: svcCat.color, background: svcCat.bg }
                                : {}}
                            >
                              {t.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="appt-footer">
              <button
                onClick={handleNext}
                disabled={!validateStep1()}
                className="appt-btn-primary"
                id="appt-next-step1"
              >
                {lang === 'np' ? 'विवरणमा जानुहोस् →' : 'Continue to Details →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Personal Details */}
        {step === 2 && (
          <div className="appt-step-content appt-fade-in">
            <div className="appt-card appt-form-card">
              <div className="appt-card-header">
                <User size={20} />
                <h3>{lang === 'np' ? 'तपाईंको विवरण' : 'Your Information'}</h3>
              </div>

              <div className="appt-booking-summary">
                <div><Calendar size={14} /> {formatDateDisplay(selectedDate)}</div>
                <div><Clock size={14} /> {selectedTime}</div>
                <div><Sparkles size={14} /> {selectedServiceLabel}</div>
              </div>

              <div className="appt-form-grid">
                <div className="appt-field">
                  <label><User size={14} /> {lang === 'np' ? 'पूरा नाम *' : 'Full Name *'}</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder={lang === 'np' ? 'तपाईंको पूरा नाम' : 'Your full name'}
                    className={errors.name ? 'error' : ''}
                    id="appt-name"
                  />
                  {errors.name && <span className="appt-error">{errors.name}</span>}
                </div>

                <div className="appt-field">
                  <label><Mail size={14} /> {lang === 'np' ? 'इमेल ठेगाना *' : 'Email Address *'}</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="your@email.com"
                    className={errors.email ? 'error' : ''}
                    id="appt-email"
                  />
                  {errors.email && <span className="appt-error">{errors.email}</span>}
                </div>

                <div className="appt-field">
                  <label><Phone size={14} /> {lang === 'np' ? 'फोन नम्बर *' : 'Phone Number *'}</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+61 4XX XXX XXX"
                    className={errors.phone ? 'error' : ''}
                    id="appt-phone"
                  />
                  {errors.phone && <span className="appt-error">{errors.phone}</span>}
                </div>

                <div className="appt-field appt-field-full">
                  <label><MessageSquare size={14} /> {lang === 'np' ? 'सन्देश (ऐच्छिक)' : 'Message (Optional)'}</label>
                  <textarea
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder={lang === 'np' ? 'तपाईंको आवश्यकता वर्णन गर्नुहोस्...' : 'Describe your specific requirements...'}
                    rows={4}
                    id="appt-message"
                  />
                </div>
              </div>
            </div>

            <div className="appt-footer">
              <button onClick={() => setStep(1)} className="appt-btn-outline">
                {lang === 'np' ? '← फिर्ता' : '← Back'}
              </button>
              <button onClick={handleNext} className="appt-btn-primary" id="appt-next-step2">
                {lang === 'np' ? 'बुकिङ समीक्षा →' : 'Review Booking →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Confirm */}
        {step === 3 && (
          <div className="appt-step-content appt-fade-in">
            <div className="appt-card appt-review-card">
              <div className="appt-card-header">
                <CheckCircle size={20} />
                <h3>Review Your Booking</h3>
              </div>

              <div className="appt-review-grid">
                <div className="appt-review-section">
                  <h4>{lang === 'np' ? 'सत्रको विवरण' : 'Session Details'}</h4>
                  <div className="appt-review-row">
                    <Calendar size={16} /> <span>{lang === 'np' ? 'मिति' : 'Date'}</span>
                    <strong>{formatDateDisplay(selectedDate)}</strong>
                  </div>
                  <div className="appt-review-row">
                    <Clock size={16} /> <span>{lang === 'np' ? 'समय' : 'Time'}</span>
                    <strong>{selectedTime}</strong>
                  </div>
                  <div className="appt-review-row">
                    <Sparkles size={16} /> <span>{lang === 'np' ? 'सेवा' : 'Service'}</span>
                    <strong>{selectedServiceLabel}</strong>
                  </div>
                </div>

                <div className="appt-review-section">
                  <h4>{lang === 'np' ? 'तपाईंको विवरण' : 'Your Information'}</h4>
                  <div className="appt-review-row">
                    <User size={16} /> <span>{lang === 'np' ? 'नाम' : 'Name'}</span>
                    <strong>{form.name}</strong>
                  </div>
                  <div className="appt-review-row">
                    <Mail size={16} /> <span>{lang === 'np' ? 'इमेल' : 'Email'}</span>
                    <strong>{form.email}</strong>
                  </div>
                  <div className="appt-review-row">
                    <Phone size={16} /> <span>{lang === 'np' ? 'फोन' : 'Phone'}</span>
                    <strong>{form.phone}</strong>
                  </div>
                  {form.message && (
                    <div className="appt-review-message">
                      <MessageSquare size={16} /> <span>{form.message}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="appt-review-notice">
                <Sparkles size={16} />
                {lang === 'np'
                  ? 'तपाईंको अपोइन्टमेन्ट प्रकाश उपाध्यायले २४ घण्टाभित्र इमेलमार्फत पुष्टि गर्नुहुनेछ।'
                  : 'Your appointment will be confirmed by Prakash Upadhyaya within 24 hours via email.'}
              </div>
            </div>

            <div className="appt-footer">
              <button onClick={() => setStep(2)} className="appt-btn-outline">
                {lang === 'np' ? '← विवरण सम्पादन' : '← Edit Details'}
              </button>
              <button onClick={handleSubmit} className="appt-btn-primary appt-btn-confirm" id="appt-confirm-submit">
                <CheckCircle size={18} />
                {lang === 'np' ? 'अपोइन्टमेन्ट पुष्टि गर्नुहोस्' : 'Confirm Appointment'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Appointment;
